"use client";

import { useRef, useEffect, useState, useCallback } from "react";
import { cn } from "@/lib/utils";

export interface FlowStep {
  text: string;
  type?: "start" | "decision" | "action" | "result";
  branches?: Array<{
    label: string;
    steps: FlowStep[];
  }>;
}

interface DiagnosticFlowchartProps {
  steps: FlowStep;
  title?: string;
}

/* ─── Node ─── */

function NodeBox({ text, type = "action" }: { text: string; type?: string }) {
  const styles: Record<string, string> = {
    start:
      "bg-blue-600 text-white border-blue-700 rounded-2xl font-semibold shadow-md",
    decision:
      "bg-white text-blue-800 border-blue-400 border-2 rounded-xl shadow-sm",
    action: "bg-blue-50 text-slate-700 border-blue-200 rounded-lg",
    result:
      "bg-slate-100 text-slate-800 border-slate-300 rounded-lg font-semibold shadow-sm",
  };

  return (
    <div
      className={cn(
        "px-4 py-2.5 text-center text-xs leading-relaxed border whitespace-pre-line",
        styles[type] || styles.action
      )}
      style={{ maxWidth: 200, minWidth: 80 }}
    >
      {text}
    </div>
  );
}

/* ─── SVG overlay ─── */

interface Line {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  label?: string;
}

function SvgOverlay({
  lines,
  width,
  height,
}: {
  lines: Line[];
  width: number;
  height: number;
}) {
  if (!lines.length) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={width}
      height={height}
      style={{ overflow: "visible" }}
    >
      {lines.map((line, i) => (
        <g key={i}>
          <line
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke="#cbd5e1"
            strokeWidth="1.5"
          />
          {line.label && (() => {
            const lw = measureText(line.label) + 12;
            const lh = 18;
            const cx = (line.x1 + line.x2) / 2;
            const cy = (line.y1 + line.y2) / 2;
            return (
              <>
                <rect
                  x={cx - lw / 2}
                  y={cy - lh / 2}
                  width={lw}
                  height={lh}
                  rx={9}
                  fill="#eff6ff"
                  stroke="#bfdbfe"
                  strokeWidth="1"
                />
                <text
                  x={cx}
                  y={cy + 4}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="600"
                  fill="#2563eb"
                >
                  {line.label}
                </text>
              </>
            );
          })()}
        </g>
      ))}
    </svg>
  );
}

function measureText(text: string): number {
  return text.length * 6 + 4;
}

/* ─── Recursive renderer ─── */

function BranchColumn({
  steps,
  onRef,
}: {
  steps: FlowStep[];
  onRef?: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div className="flex flex-col items-center" ref={onRef}>
      {steps.map((step, j) => (
        <div key={j} className="flex flex-col items-center">
          {j > 0 && <div className="w-px h-14 bg-slate-300" />}
          <RenderStep step={step} />
        </div>
      ))}
    </div>
  );
}

function RenderStep({ step }: { step: FlowStep }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const parentNodeRef = useRef<HTMLDivElement>(null);
  const branchRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  const computeLines = useCallback(() => {
    if (
      !containerRef.current ||
      !parentNodeRef.current ||
      !step.branches?.length
    )
      return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const parentRect = parentNodeRef.current.getBoundingClientRect();
    const newLines: Line[] = [];

    const px = parentRect.left + parentRect.width / 2 - containerRect.left;
    const py = parentRect.bottom - containerRect.top;

    step.branches.forEach((branch, i) => {
      const branchEl = branchRefs.current[i];
      if (!branchEl) return;

      const firstNode = branchEl.querySelector("[data-node]") as HTMLElement;
      if (!firstNode) return;

      const nodeRect = firstNode.getBoundingClientRect();
      const cx = nodeRect.left + nodeRect.width / 2 - containerRect.left;
      const cy = nodeRect.top - containerRect.top;

      const midY = py + (cy - py) * 0.4;

      // Vertical from parent to midY
      newLines.push({ x1: px, y1: py, x2: px, y2: midY });
      // Horizontal at midY
      if (Math.abs(px - cx) > 1) {
        newLines.push({ x1: px, y1: midY, x2: cx, y2: midY });
      }
      // Vertical from midY to child (with label)
      newLines.push({
        x1: cx,
        y1: midY,
        x2: cx,
        y2: cy,
        label: branch.label,
      });
    });

    setLines(newLines);
    setDims({
      w: containerRect.width,
      h: containerRect.height,
    });
  }, [step.branches]);

  useEffect(() => {
    const timer = setTimeout(computeLines, 60);
    window.addEventListener("resize", computeLines);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", computeLines);
    };
  }, [computeLines]);

  if (!step.branches || step.branches.length === 0) {
    return (
      <div data-node>
        <NodeBox text={step.text} type={step.type} />
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <SvgOverlay lines={lines} width={dims.w} height={dims.h} />

      <div className="flex flex-col items-center">
        <div data-node ref={parentNodeRef}>
          <NodeBox text={step.text} type={step.type} />
        </div>

        {/* Spacer for connector lines */}
        <div style={{ height: 72 }} />

        {/* Branches */}
        <div
          className="flex justify-center"
          style={{ gap: step.branches.length > 2 ? 28 : 48 }}
        >
          {step.branches.map((branch, i) => (
            <BranchColumn
              key={i}
              steps={branch.steps}
              onRef={(el) => {
                branchRefs.current[i] = el;
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Root ─── */

export function DiagnosticFlowchart({
  steps,
  title,
}: DiagnosticFlowchartProps) {
  return (
    <div className="rounded-xl border bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 p-8 overflow-x-auto">
      {title && (
        <div className="text-center text-sm font-semibold text-slate-600 mb-5">
          {title}
        </div>
      )}
      <div className="flex justify-center" style={{ minWidth: 500 }}>
        <RenderStep step={steps} />
      </div>
    </div>
  );
}
